/**
 * The map's scenery data: the 520 mountains, the three field squares and the
 * server's collision circle for each mountain. The rows are legacy Game.js's
 * doodads, groundDoodads and walls tables, value for value. ab-server's
 * MOUNTAIN_OBJECTS holds the same 520 circles sorted by x and loads each as a
 * hit circle, so collisions are the server's; here the walls only feed the
 * autopilot's grid. Doodads.ts places and culls the sprites.
 */
import type { TextureConfigName } from '../Textures';

/** The four mountain textures. */
export type MountainTexture = Extract<TextureConfigName, 'mountain1' | 'mountain2' | 'mountain3' | 'mountain4'>;

/** The textures a doodad can wear: a mountain, or the field square under a base. */
export type DoodadTexture = MountainTexture | Extract<TextureConfigName, 'doodadField'>;

/** A piece of scenery as Doodads.ts places it: world centre, texture and look. */
export interface Doodad {
  readonly x: number;
  readonly y: number;
  readonly textureName: DoodadTexture;
  readonly scale: number;
  /** Rotation in radians. */
  readonly rotation: number;
  /** Sprite alpha, or null for the sprite's own. */
  readonly alpha: number | null;
  /** Sprite tint, or null for none. */
  readonly tint: number | null;
  /** A mountain has a collision circle and pulls shadows in; a field square has neither and hides on the title screen. */
  readonly isMountain: boolean;
}

/** A mountain's collision circle, as ab-server holds it. */
export interface Wall {
  readonly x: number;
  readonly y: number;
  readonly radius: number;
}

/**
 * One row in legacy's order: centre, texture and scale, then the rotation in
 * radians, the alpha and the tint only where a row sets them.
 */
type DoodadRow<T extends DoodadTexture> = readonly [
  x: number,
  y: number,
  texture: T,
  scale: number,
  rotation?: number,
  alpha?: number,
  tint?: number,
];

/**
 * The server's collision radius per unit of a mountain's sprite scale: every one
 * of ab-server's 520 circles is 120 times the scale of the mountain at its centre.
 */
const WALL_RADIUS_PER_SCALE = 120;

/** The doodads of one table, with the defaults a row leaves out filled in. */
function toDoodads(rows: readonly DoodadRow<DoodadTexture>[], isMountain: boolean): readonly Doodad[] {
  return rows.map(([x, y, textureName, scale, rotation = 0, alpha, tint]) => ({
    x,
    y,
    textureName,
    scale,
    rotation,
    alpha: alpha ?? null,
    tint: tint ?? null,
    isMountain,
  }));
}

/** The 520 mountains, in legacy's order. */
const MOUNTAIN_ROWS: readonly DoodadRow<MountainTexture>[] = [
  [1009, -2308, 'mountain1', 0.9, 0.2],
  [1241, -2490, 'mountain4', 0.5, -0.2],
  [1157, -2379, 'mountain2', 0.7, -0.1],
  [622, -2126, 'mountain3', 0.4],
  [669, -2187, 'mountain2', 0.6],
  [-392, -1669, 'mountain1', 1.1, 0.1],
  [-273, -1746, 'mountain4', 0.5, -0.1],
  [-252, -1504, 'mountain2', 1],
  [1553, -2016, 'mountain2', 0.4, 0.2],
  [1637, -1972, 'mountain1', 0.5, 0.1],
  [1736, -1922, 'mountain2', 0.5, 0.7],
  [2150, -2406, 'mountain2', 0.6],
  [2238, -2318, 'mountain3', 0.9],
  [2364, -2391, 'mountain4', 0.6, 0],
  [2491, -2682, 'mountain1', 0.6, -0.1],
  [2596, -2671, 'mountain2', 0.9, 0.2],
  [-150, -3147, 'mountain4', 0.4, -0.4],
  [-155, -3044, 'mountain2', 0.7, 0],
  [-427, -3600, 'mountain2', 0.4],
  [-259, -2982, 'mountain2', 0.5],
  [-379, -3529, 'mountain1', 0.6, 0.1, 1],
  [-665, -3052, 'mountain2', 0.5],
  [20, -1816, 'mountain4', 0.5, 0.3],
  [127, -1799, 'mountain1', 0.5],
  [263, -2572, 'mountain3', 0.4, 0.4],
  [405, -2570, 'mountain1', 0.9, -0.2],
  [851, -4183, 'mountain1', 1],
  [754, -3971, 'mountain2', 0.9, -0.1, 1],
  [1757, -5065, 'mountain4', 1.1],
  [1169, -4453, 'mountain3', 0.9],
  [2054, -5244, 'mountain3', 0.9],
  [1631, -4901, 'mountain1', 0.9, 0],
  [2305, -5281, 'mountain2', 0.9, 0.2, 1],
  [1007, -4281, 'mountain1', 0.8, 0.1],
  [2766, -5202, 'mountain1', 0.8, 0.1, 1],
  [2927, -5204, 'mountain2', 0.7, 0, 1],
  [3206, -5218, 'mountain2', 0.3, 0.6, 1],
  [3099, -5193, 'mountain4', 0.6, 0.1, 1],
  [1417, -4726, 'mountain2', 1],
  [2844, -1513, 'mountain4', 0.5, 0.1],
  [3206, -1464, 'mountain1', 1, 0.1, 1],
  [2881, -1403, 'mountain2', 0.9],
  [3804, -2025, 'mountain1', 0.7, -0.1],
  [4116, -1778, 'mountain2', 0.9, -0.3],
  [3715, -1508, 'mountain3', 0.6, 0.1],
  [4247, -1126, 'mountain1', 0.6, 0.1, 1],
  [3860, 268, 'mountain1', 0.5, 0.5, 1],
  [4334, -1011, 'mountain2', 1.1, -0.1],
  [3849, 349, 'mountain3', 0.6],
  [3956, 490, 'mountain4', 0.8, -0.4],
  [4073, 667, 'mountain2', 0.9, -0.5],
  [3583, -864, 'mountain4', 0.8, 0.1, 0.8, 0xefecb9],
  [4135, 836, 'mountain2', 1, -0.2],
  [4785, -743, 'mountain2', 1, 0.1],
  [4993, -839, 'mountain1', 0.9, 0.3, 1],
  [5224, -482, 'mountain3', 0.7],
  [5235, -1238, 'mountain4', 1.1, 0, 1],
  [5419, -1346, 'mountain2', 0.6, -0.5, 0.8],
  [6075, -5099, 'mountain1', 0.8],
  [5767, -4953, 'mountain2', 0.9, -0.3, 1],
  [5896, -4967, 'mountain2', 0.9, 0.1, 1],
  [5384, -4642, 'mountain4', 0.9],
  [5704, -4857, 'mountain4', 0.9],
  [5563, -4697, 'mountain2', 0.9],
  [5406, -4470, 'mountain1', 1.1],
  [5352, -3964, 'mountain3', 0.9],
  [5309, -3665, 'mountain1', 1.1],
  [5247, -3464, 'mountain4', 0.9, -0.5],
  [5300, -3121, 'mountain1', 0.9],
  [3524, -3340, 'mountain1', 1.1, 0],
  [3661, -3589, 'mountain2', 0.9, -0.7],
  [7236, -1376, 'mountain2', 0.6, -0.3],
  [7624, -1610, 'mountain2', 0.8],
  [7403, -1555, 'mountain2', 0.7],
  [7514, -1568, 'mountain2', 0.9, 0],
  [3660, -2705, 'mountain1', 0.9, 0.2],
  [3374, -2813, 'mountain2', 0.8],
  [7347, -1447, 'mountain1', 0.9, 0.1],
  [7236, -775, 'mountain4', 0.5, -0.9],
  [7207, -631, 'mountain2', 0.9, 0],
  [7303, -468, 'mountain1', 0.9, -0.3],
  [7262, -1263, 'mountain4', 0.9, -0.6],
  [7404, -350, 'mountain2', 1, 0.6],
  [7589, -305, 'mountain3', 0.9, 0.6],
  [7741, -1589, 'mountain4', 1, 0.2],
  [7949, -1594, 'mountain1', 1, -0.2],
  [8152, -1599, 'mountain2', 1.1, 0.1],
  [8378, -1602, 'mountain4', 1.2, 0],
  [7873, -321, 'mountain2', 0.6, 0.3],
  [8543, -1661, 'mountain1', 0.7, 0],
  [7790, -259, 'mountain1', 1, 0.1],
  [8675, -1573, 'mountain2', 1, 0.1],
  [8163, -245, 'mountain2', 0.7, 0.3],
  [8329, -311, 'mountain2', 0.7, 0.5],
  [8275, -229, 'mountain1', 1, 0.1],
  [8447, -277, 'mountain3', 1, 0],
  [8824, -1447, 'mountain1', 0.9, -0.4],
  [7221, -1140, 'mountain1', 0.5, -0.2],
  [8924, -1273, 'mountain2', 0.9, -0.5],
  [6844, -950, 'mountain3', 1, 0.1],
  [8949, -1060, 'mountain3', 1.1, -0.3],
  [8904, -920, 'mountain4', 0.8, -0.7],
  [8582, -338, 'mountain2', 0.7],
  [8963, -803, 'mountain1', 1, -0.4],
  [8680, -322, 'mountain4', 0.9, 0],
  [8811, -449, 'mountain1', 0.9, -0.3],
  [8910, -610, 'mountain2', 0.9, -0.4],
  [6855, 114, 'mountain1', 0.8],
  [6971, 241, 'mountain2', 1, -0.1],
  [6852, 656, 'mountain2', 0.5, 0.3],
  [6980, 706, 'mountain1', 0.9, -0.4],
  [6946, 939, 'mountain4', 1, -0.2],
  [6027, -560, 'mountain1', 0.6, 0],
  [7521, 425, 'mountain2', 0.4, 0.1],
  [7599, 389, 'mountain1', 0.5, -0.5],
  [5863, -431, 'mountain2', 0.9, 0.1],
  [9392, 262, 'mountain2', 0.9, -0.1],
  [7521, 512, 'mountain3', 0.6],
  [9807, 1027, 'mountain1', 0.5, -0.1],
  [9554, 237, 'mountain1', 1, -0.2, 1],
  [9346, 392, 'mountain4', 1.2, -0.3],
  [9789, 1142, 'mountain3', 0.7],
  [9747, -532, 'mountain4', 0.8, 0.2, 1],
  [8591, 347, 'mountain4', 0.6, -0.2],
  [9951, -509, 'mountain1', 1],
  [9308, 2417, 'mountain1', 0.5, 0.1],
  [10185, -522, 'mountain2', 0.9, 0, 1],
  [10330, 2147, 'mountain1', 0.6, 0],
  [9350, 2480, 'mountain3', 0.7],
  [10503, 2124, 'mountain2', 0.9],
  [12500, 2628, 'mountain4', 0.5, 0.3],
  [13188, 2864, 'mountain4', 0.5, -0.1],
  [12637, 2659, 'mountain2', 0.7, 0.1],
  [13262, 2899, 'mountain1', 0.5],
  [13777, 5168, 'mountain1', 0.5, -0.3],
  [15709, 6399, 'mountain1', 0.4],
  [13539, 5664, 'mountain3', 0.5, 0, 1],
  [15660, 6474, 'mountain2', 0.4, -0.3],
  [13743, 5248, 'mountain2', 0.7, -0.1],
  [15482, 6600, 'mountain2', 0.4, -0.1],
  [15591, 6525, 'mountain4', 0.3],
  [13487, 5738, 'mountain4', 0.7],
  [15407, 6702, 'mountain1', 0.6, 0.1],
  [8171, -2568, 'mountain2', 0.5, 0.1],
  [16001, 6015, 'mountain3', 0.6],
  [16017, 6110, 'mountain1', 0.4, 0, 1],
  [6496, -1491, 'mountain1', 0.5, 0.3],
  [6626, -1480, 'mountain2', 0.7],
  [6190, -1022, 'mountain4', 0.8],
  [8325, -2615, 'mountain2', 1, -0.3],
  [8222, -2412, 'mountain4', 1],
  [9204, -2288, 'mountain1', 0.9, -0.1],
  [9279, -2216, 'mountain2', 1],
  [10375, -1558, 'mountain2', 0.8, -0.3],
  [10309, -1421, 'mountain1', 0.9],
  [10247, -1216, 'mountain4', 1.2, -0.5],
  [10079, -2310, 'mountain4', 0.9, 0.3],
  [10320, -2330, 'mountain3', 1],
  [10942, -2963, 'mountain2', 0.9, -0.3],
  [10807, -2778, 'mountain4', 1.1, -0.5],
  [12989, -1929, 'mountain3', 0.6],
  [12613, -1181, 'mountain1', 0.4, 0, 1],
  [12559, -1120, 'mountain2', 0.5],
  [11642, -1900, 'mountain4', 0.7, -0.5],
  [11558, -1692, 'mountain1', 0.9, -0.2],
  [11509, -1479, 'mountain2', 0.5],
  [12559, -2673, 'mountain1', 0.8],
  [12446, -2487, 'mountain4', 1, -0.5],
  [12375, -2303, 'mountain3', 0.9, -0.2],
  [10363, -3514, 'mountain4', 0.7, -0.4],
  [10290, -3340, 'mountain1', 0.9, -0.2],
  [10162, -3207, 'mountain2', 0.7],
  [9003, -3048, 'mountain4', 0.9, -0.1],
  [9161, -3119, 'mountain1', 0.6],
  [14550, -3462, 'mountain2', 0.9, -0.2],
  [14407, -3335, 'mountain4', 0.9, -0.2],
  [14366, -4493, 'mountain2', 0.9, 0.5],
  [14477, -4437, 'mountain3', 1],
  [15305, -4230, 'mountain4', 0.9, 0.1],
  [15481, -4283, 'mountain2', 0.9],
  [15349, -5009, 'mountain4', 0.4, 0.3],
  [11874, -4879, 'mountain2', 0.6],
  [15453, -4984, 'mountain1', 0.7],
  [11907, -4742, 'mountain4', 0.8, -0.6],
  [12440, -4278, 'mountain4', 0.5, 0.3],
  [11980, -4582, 'mountain2', 0.9],
  [12131, -4387, 'mountain1', 1.1],
  [15681, -4973, 'mountain4', 1],
  [12591, -4252, 'mountain1', 0.8],
  [12777, -4244, 'mountain2', 0.9, 0.2],
  [12969, -4227, 'mountain3', 0.9],
  [15897, -5071, 'mountain2', 0.9, -0.2],
  [13204, -4228, 'mountain4', 1],
  [11592, -5261, 'mountain1', 0.7, 0.2],
  [12743, -4826, 'mountain1', 0.4, 0.3],
  [10102, -5078, 'mountain2', 0.7, 0.4],
  [12854, -4782, 'mountain3', 0.9, 0.1],
  [10191, -5033, 'mountain1', 0.7],
  [10523, -5133, 'mountain2', 1],
  [10336, -4977, 'mountain4', 1.1, -0.3],
  [10667, -5250, 'mountain2', 0.8, -0.1],
  [9665, -6403, 'mountain4', 0.7, 0.3],
  [10798, -5379, 'mountain1', 0.5],
  [9670, -5547, 'mountain1', 0.9, -0.1],
  [9834, -6369, 'mountain3', 1],
  [9864, -5572, 'mountain2', 1.1, -0.1],
  [11362, -3957, 'mountain2', 0.9, 0, 1],
  [11162, -3830, 'mountain4', 1, -0.1],
  [8922, -6173, 'mountain4', 1],
  [9003, -5368, 'mountain1', 0.5],
  [8453, -6153, 'mountain1', 0.6, 0.2],
  [8954, -5229, 'mountain3', 0.9, -0.2],
  [8905, -5072, 'mountain1', 0.8, -0.3],
  [8704, -3873, 'mountain3', 0.9],
  [8578, -6105, 'mountain2', 0.8, 0.4],
  [8508, -3710, 'mountain1', 0.7, 0],
  [8614, -5400, 'mountain4', 0.5],
  [8788, -4922, 'mountain4', 1.1],
  [8936, -3905, 'mountain4', 0.6, 0.3],
  [9701, -4613, 'mountain4', 0.9, -0.1],
  [6973, -4776, 'mountain2', 0.6],
  [7016, -4674, 'mountain1', 0.9],
  [9124, -3853, 'mountain1', 0.9, 0.2],
  [7253, -4648, 'mountain4', 0.8, 0.2],
  [6602, -4591, 'mountain1', 0.6, 0.2],
  [8510, -5322, 'mountain2', 0.9],
  [6687, -3810, 'mountain4', 0.7, 0.3],
  [9525, -4492, 'mountain3', 1.1],
  [7461, -4705, 'mountain2', 0.9],
  [9280, -3812, 'mountain2', 0.8, -0.3],
  [6467, -2811, 'mountain1', 0.3],
  [6842, -3820, 'mountain1', 0.8, 0.3],
  [6483, -2725, 'mountain4', 0.6, 0.3],
  [7675, -4864, 'mountain1', 1.1],
  [6991, -2885, 'mountain4', 0.9, 0.2],
  [6610, -2664, 'mountain4', 0.9],
  [6822, -2736, 'mountain2', 0.9],
  [6763, -4582, 'mountain3', 0.9],
  [6509, -2490, 'mountain2', 0.9, -0.3],
  [7216, -3865, 'mountain4', 1.1, 0.2, 1],
  [7018, -3708, 'mountain2', 1, 0.2],
  [2254, -3301, 'mountain1', 0.8, 0.5],
  [6378, -2310, 'mountain1', 0.8, 0.2],
  [7197, -2857, 'mountain2', 0.9],
  [2609, -3483, 'mountain4', 0.5],
  [2449, -3385, 'mountain2', 0.9, 0.3, 1],
  [4585, -2889, 'mountain4', 0.7, -0.2],
  [4470, -2768, 'mountain3', 0.8, 0.2],
  [4083, -4033, 'mountain1', 0.9, 0.5, 1],
  [1568, -2869, 'mountain1', 0.4],
  [4336, -4105, 'mountain2', 1.2],
  [1977, -1678, 'mountain2', 0.5],
  [1412, -3642, 'mountain1', 0.5, 0, 1],
  [1570, -2792, 'mountain4', 0.6],
  [1932, -1586, 'mountain1', 0.6],
  [1310, -3547, 'mountain2', 0.6, 0],
  [3611, 1391, 'mountain2', 1],
  [3558, 2174, 'mountain2', 0.9],
  [3431, 1563, 'mountain1', 0.9, 0.3],
  [3142, 2813, 'mountain2', 0.7],
  [3330, 2296, 'mountain4', 1.1],
  [3001, 2818, 'mountain1', 0.6, 0.3],
  [3703, 2044, 'mountain1', 0.4],
  [2872, 3863, 'mountain1', 0.9, -0.2],
  [3125, 2942, 'mountain3', 1.1],
  [2841, 4018, 'mountain4', 1.1, 0.1],
  [2402, 5140, 'mountain4', 0.6, 0.3],
  [2511, 5167, 'mountain3', 0.8],
  [-14607, -5112, 'mountain4', 0.9, 0.1],
  [-14430, -5180, 'mountain1', 0.9, 0],
  [-14197, -5222, 'mountain2', 1.2, -0.1],
  [-14895, -4703, 'mountain4', 0.5, 0.2],
  [-14797, -4728, 'mountain2', 0.4],
  [-14697, -4739, 'mountain1', 0.4],
  [-13919, -5281, 'mountain1', 1.1, 0],
  [-13646, -5170, 'mountain3', 0.7],
  [-13400, -5068, 'mountain2', 1.2, 0.2],
  [-13099, -5108, 'mountain4', 1.1, 0.2],
  [-12824, -5092, 'mountain1', 0.9],
  [-12631, -5044, 'mountain2', 0.9, -0.2],
  [-12427, -4914, 'mountain1', 0.9],
  [-12270, -4816, 'mountain2', 0.7, -0.3],
  [-11772, -4983, 'mountain2', 0.7, 0],
  [-11940, -4867, 'mountain1', 0.9],
  [-12091, -4699, 'mountain3', 0.9],
  [-12270, -4529, 'mountain4', 0.7],
  [-12460, -4396, 'mountain1', 0.9, 0.4],
  [-13058, -4252, 'mountain1', 0.9],
  [-12894, -4096, 'mountain4', 0.7, 0.2],
  [-12738, -4077, 'mountain2', 0.7],
  [-13546, -4341, 'mountain1', 0.4],
  [-13428, -4299, 'mountain3', 0.9],
  [-14679, -4192, 'mountain1', 0.7, -0.1],
  [-14368, -4308, 'mountain4', 0.7, -0.2],
  [-14495, -4133, 'mountain2', 1.1, -0.1],
  [-12072, -3824, 'mountain2', 0.8, -0.1],
  [-11904, -3648, 'mountain1', 1, 0],
  [-11654, -3569, 'mountain3', 0.7],
  [-11648, -3357, 'mountain4', 0.9, 0],
  [-11420, -3359, 'mountain1', 0.9],
  [-11296, -3135, 'mountain2', 0.7],
  [-10782, -2838, 'mountain1', 0.8, -0.1],
  [-11410, -3039, 'mountain1', 0.3, 0.6],
  [-10581, -2773, 'mountain2', 1.1, 0.1],
  [-11118, -5114, 'mountain4', 0.8, 0.2],
  [-10675, -5079, 'mountain1', 0.9],
  [-10205, -4890, 'mountain1', 0.8, 0.1],
  [-11543, -4164, 'mountain4', 0.8, 0.3],
  [-11287, -4244, 'mountain2', 0.5],
  [-10018, -4747, 'mountain2', 1, -0.2],
  [-9278, 419, 'mountain3', 0.5],
  [-9341, 470, 'mountain4', 0.5, 0.3],
  [-9180, 496, 'mountain1', 0.8, 0.6],
  [-11365, -4131, 'mountain3', 1],
  [-9353, 0, 'mountain3', 0.6, 0.3],
  [-8975, 528, 'mountain2', 1, 0.1],
  [-9413, 89, 'mountain4', 0.9],
  [-8230, 770, 'mountain1', 0.4],
  [-9231, 17, 'mountain1', 0.7, 0],
  [-6808, 1667, 'mountain3', 0.5, -0.2],
  [-6694, 1622, 'mountain2', 0.8, -0.4],
  [-8285, 855, 'mountain3', 0.6],
  [-6793, 1796, 'mountain2', 0.8, 0.4],
  [-6865, 1935, 'mountain1', 0.5, -0.1],
  [-7043, 2222, 'mountain3', 0.6, -0.2],
  [-7083, 2368, 'mountain1', 0.6, -0.2],
  [-7103, 2533, 'mountain4', 0.7, -0.4],
  [-7099, 2721, 'mountain2', 0.8, -0.3],
  [-7178, 2874, 'mountain1', 0.4],
  [-6964, 3110, 'mountain4', 0.7, 0.2],
  [-6246, 4191, 'mountain1', 0.6, -0.1],
  [-6735, 3446, 'mountain1', 0.4, 0.1],
  [-6722, 3537, 'mountain4', 0.7, 0.4],
  [-6396, 3388, 'mountain4', 0.9],
  [-6787, 3140, 'mountain2', 0.9],
  [-6227, 4488, 'mountain2', 0.7, -0.5],
  [-6281, 3953, 'mountain1', 0.6, 0.2],
  [-6530, 3576, 'mountain1', 1.1],
  [-6147, 3994, 'mountain3', 1.1],
  [-6141, 4292, 'mountain2', 1.2, -0.3],
  [-6377, 5578, 'mountain2', 0.7, -0.5],
  [-6152, 4569, 'mountain1', 0.9, -0.2],
  [-6222, 4769, 'mountain4', 0.7, -0.5],
  [-6266, 4956, 'mountain1', 0.6, -0.2],
  [-6233, 5414, 'mountain1', 0.9, -0.4],
  [-6347, 5646, 'mountain3', 0.7, -0.1, 1],
  [-6400, 6066, 'mountain2', 0.7, -0.2],
  [-6411, 6261, 'mountain1', 0.8],
  [-6565, 7043, 'mountain1', 0.6, -0.3],
  [-6377, 6415, 'mountain4', 0.9, -0.3],
  [-6440, 6614, 'mountain1', 0.7, -0.2],
  [-5944, 1954, 'mountain3', 0.5, -0.4],
  [-6001, 2039, 'mountain1', 0.8, 0.2],
  [-6467, 7070, 'mountain3', 0.9],
  [-4963, 3608, 'mountain1', 0.6, -0.3, 1],
  [-4776, 3510, 'mountain2', 0.4, -0.1, 1],
  [-5785, 1998, 'mountain2', 1.1, -0.2],
  [-3708, 3449, 'mountain1', 0.6, -0.3],
  [-3790, 3582, 'mountain1', 0.9],
  [-4031, 3697, 'mountain4', 0.7, -0.1],
  [-4859, 3634, 'mountain3', 1],
  [-3850, 3730, 'mountain2', 1, 0.2],
  [-4689, 4605, 'mountain4', 0.9, -0.2],
  [-3492, 2892, 'mountain1', 0.6, -0.2],
  [-4507, 4483, 'mountain1', 0.6],
  [-4829, 2745, 'mountain1', 0.9, -0.2],
  [-4435, 4273, 'mountain2', 0.9, -0.6],
  [-3383, 2930, 'mountain2', 0.9],
  [-4888, 2821, 'mountain2', 0.9, 0.1],
  [-5372, 5172, 'mountain1', 0.5],
  [-5036, 2981, 'mountain4', 0.9, 0.3],
  [-9895, -1942, 'mountain1', 1, 0.1],
  [-9371, -2159, 'mountain4', 0.6],
  [-9995, -1773, 'mountain4', 0.9, -0.5],
  [-5280, 5218, 'mountain4', 0.9, 0],
  [-5079, 5183, 'mountain1', 0.9, -0.3],
  [-5149, 3079, 'mountain1', 0.6, 0.2],
  [-9695, -2048, 'mountain2', 1, -0.1],
  [-5063, 5374, 'mountain2', 0.9, -0.2],
  [-9504, -2059, 'mountain1', 1, 0.2],
  [-8948, -2140, 'mountain4', 0.7, 0.3],
  [-9262, -2081, 'mountain2', 1.1, 0.4],
  [-9068, -2071, 'mountain4', 1, 0.2],
  [-8861, -2045, 'mountain2', 1.1, 0],
  [-8654, -2083, 'mountain1', 1, 0.3],
  [-8473, -2048, 'mountain3', 1, 0],
  [-8283, -1965, 'mountain1', 1, 0.5],
  [-8182, -1863, 'mountain2', 0.9, -0.2],
  [-10037, -1579, 'mountain1', 0.9, -0.4],
  [-10063, -1365, 'mountain2', 0.9, -0.1],
  [-10074, -1190, 'mountain1', 0.9, -0.4],
  [-8167, -1225, 'mountain1', 0.5, -0.7],
  [-8188, -1087, 'mountain4', 0.8, -0.5],
  [-9975, -1022, 'mountain1', 0.9, 0.3],
  [-9835, -910, 'mountain2', 1, 0.1],
  [-9709, -848, 'mountain4', 1, 0.3],
  [-9532, -792, 'mountain2', 1, 0.1],
  [-9348, -779, 'mountain1', 1, 0],
  [-9131, -784, 'mountain2', 1.1, 0.1],
  [-8754, -795, 'mountain3', 0.7],
  [-8587, -797, 'mountain4', 1, 0],
  [-8424, -856, 'mountain1', 0.9],
  [-8246, -921, 'mountain2', 0.9, -0.4],
  [-8153, -1701, 'mountain4', 0.7, -0.7],
  [-6447, -2137, 'mountain1', 0.6, -0.2],
  [-8153, -1591, 'mountain1', 0.5, -0.6],
  [-7694, -1393, 'mountain4', 1, -0.1],
  [-6541, -2030, 'mountain2', 0.7],
  [-7135, -1547, 'mountain2', 0.7, 0.1],
  [-5885, -2958, 'mountain1', 0.6, 0.3],
  [-7171, -1440, 'mountain1', 0.8, -0.2],
  [-5620, -3123, 'mountain2', 0.6, -0.4],
  [-6631, -1866, 'mountain4', 0.8],
  [-5882, -3696, 'mountain2', 0.8, -0.4],
  [-5716, -2944, 'mountain3', 0.9],
  [-5949, -3528, 'mountain1', 0.5, -0.1],
  [-6869, -4052, 'mountain4', 0.8],
  [-7081, -2673, 'mountain3', 0.5],
  [-6725, -4069, 'mountain1', 0.6, -0.2],
  [-10487, -2295, 'mountain2', 0.5, 0.1, 1],
  [-7032, -2749, 'mountain2', 0.6, -0.4],
  [-10769, -3817, 'mountain3', 0.6, -0.2],
  [-6607, -4081, 'mountain2', 0.5],
  [-10849, -1824, 'mountain1', 0.7, 0.1],
  [-10479, -2146, 'mountain4', 0.9, -0.1],
  [-7003, -2611, 'mountain4', 0.9, -0.2],
  [-10749, -1511, 'mountain2', 0.6, -0.4],
  [-10697, -1953, 'mountain2', 1, 0.1],
  [-10848, -1330, 'mountain3', 0.9],
  [-10912, -1220, 'mountain4', 0.5, 0],
  [-9843, -2685, 'mountain4', 0.7],
  [-10823, -3717, 'mountain2', 0.7],
  [-9697, -2713, 'mountain1', 0.6, 0.3],
  [-10080, -3431, 'mountain2', 0.4],
  [-10648, -3844, 'mountain1', 0.9],
  [-9530, -2695, 'mountain2', 1],
  [-2387, -6791, 'mountain1', 0.9],
  [-8465, -5037, 'mountain2', 0.7],
  [-2550, -6627, 'mountain2', 0.9, -0.1],
  [-9325, -2822, 'mountain1', 0.7, -0.1],
  [-10140, -3346, 'mountain1', 0.8],
  [-8461, -4908, 'mountain4', 0.9],
  [8116, -1076, 'mountain2', 0.8, -0.3],
  [8115, -940, 'mountain4', 0.8, -0.3],
  [-2503, -6359, 'mountain4', 1.4, -0.6],
  [-9062, -1580, 'mountain2', 0.8, -0.5],
  [8120, -790, 'mountain1', 0.8],
  [-9065, -1444, 'mountain4', 0.8, -0.3],
  [-2615, -6159, 'mountain1', 1],
  [-2744, -5943, 'mountain2', 1.1],
  [-3713, -4955, 'mountain3', 0.9],
  [-9061, -1292, 'mountain1', 0.8, 0],
  [-1551, -4718, 'mountain4', 0.5],
  [5251, -6249, 'mountain1', 0.5, 0, 1],
  [5192, -6181, 'mountain2', 0.4, -0.1],
  [-2898, -5732, 'mountain3', 0.9],
  [-1705, -4629, 'mountain3', 0.8],
  [1618, -7035, 'mountain1', 0.5],
  [-3829, -4776, 'mountain4', 0.9],
  [-2851, -5589, 'mountain4', 1.1, -0.2],
  [-2974, -5399, 'mountain2', 1.1],
  [-3952, -4568, 'mountain1', 0.9],
  [-4135, -4368, 'mountain2', 1.1, 0.1, 1],
  [-3428, -5104, 'mountain1', 1.1],
  [-3167, -5254, 'mountain4', 0.9, 0.1],
  [-3049, -6916, 'mountain2', 1],
  [-3097, -6722, 'mountain1', 0.9, -0.3],
  [-3190, -6493, 'mountain3', 1],
  [-3318, -6284, 'mountain4', 1.1],
  [-4672, -5861, 'mountain1', 1.1, 1],
  [-4480, -5668, 'mountain1', 1],
  [-4294, -5548, 'mountain2', 0.9, 1.1],
  [-3433, -6072, 'mountain1', 1, -0.2],
  [-3568, -5881, 'mountain3', 1],
  [-3736, -5698, 'mountain2', 1.2],
  [-3978, -7517, 'mountain3', 0.9, 0.1],
  [-3956, -5572, 'mountain4', 1],
  [-4103, -5400, 'mountain1', 1],
  [-4281, -5240, 'mountain2', 1],
  [-4409, -5034, 'mountain3', 1],
  [-4475, -4808, 'mountain4', 0.9],
  [-2777, -7546, 'mountain3', 1, 0.8],
  [-2542, -7502, 'mountain2', 0.9, 0.4],
  [-2398, -7364, 'mountain1', 0.9, -0.7],
  [-2350, -7151, 'mountain2', 0.9, -0.4],
  [-2315, -6976, 'mountain4', 0.8, -0.8],
  [-3749, -7488, 'mountain4', 1],
  [-2995, -7596, 'mountain2', 1.2, 0.5],
  [-3253, -7558, 'mountain3', 1.1, 0.3],
  [-3468, -7472, 'mountain4', 1],
  [-5028, -6473, 'mountain2', 1, 0.4],
  [-3604, -7301, 'mountain1', 1.1],
  [-3678, -7078, 'mountain2', 1.1],
  [-3835, -6826, 'mountain3', 1.1],
  [-3935, -6595, 'mountain2', 1],
  [-5214, -7381, 'mountain3', 1],
  [-4065, -6416, 'mountain3', 1.1],
  [-4174, -6231, 'mountain4', 0.7],
  [-4556, -6940, 'mountain3', 1],
  [-4663, -6781, 'mountain4', 1],
  [-4748, -6548, 'mountain2', 1.3],
  [-4847, -6257, 'mountain2', 1.1, -0.4],
  [-4806, -6016, 'mountain4', 0.9, 1.2],
  [-4950, -7410, 'mountain1', 1.4, 0.2],
  [-5470, -7251, 'mountain4', 1.3, -0.2],
  [-5637, -7038, 'mountain2', 1, -0.3],
  [-5593, -6819, 'mountain4', 1],
  [-5406, -6727, 'mountain3', 1, 0.6],
  [-5263, -6580, 'mountain1', 1, 0.4],
  [-4636, -7476, 'mountain2', 1, 0.2],
  [-4399, -7496, 'mountain2', 0.9, -0.2],
  [-6769, -7571, 'mountain2', 0.9, -0.4],
  [-6853, -7377, 'mountain1', 0.9],
  [-8273, -7231, 'mountain4', 0.5, 0.4],
  [-4182, -7512, 'mountain4', 0.9, 0.1],
  [-7525, -5855, 'mountain2', 0.9],
  [-7495, -6329, 'mountain3', 0.5],
  [-7297, -6946, 'mountain2', 0.9],
  [-8148, -7137, 'mountain1', 0.7],
  [-7030, -7301, 'mountain4', 0.7],
];

/** The three field squares: the blue and the red CTF base, and one north of the map's centre. */
const FIELD_ROWS: readonly DoodadRow<'doodadField'>[] = [
  [-9670, -1470, 'doodadField', 0.5, 0],
  [8600, -940, 'doodadField', 0.5, 0],
  [920, -2800, 'doodadField', 0.5, 0],
];

/** The mountains, in the rows' order. */
export const mountains: readonly Doodad[] = toDoodads(MOUNTAIN_ROWS, true);

/** The field squares, in the rows' order. */
export const groundMarkers: readonly Doodad[] = toDoodads(FIELD_ROWS, false);

/** Each mountain's collision circle, in the mountains' order. */
export const walls: readonly Wall[] = mountains.map(({ x, y, scale }) => ({
  x,
  y,
  radius: WALL_RADIUS_PER_SCALE * scale,
}));
